import { makeAutoObservable, runInAction } from 'mobx'

import { restClient } from '@/Core'
import { formatAppDate } from '@/Ship/Helpers/appTime'

import type { ShiftStatus } from '../Contracts/ShiftStatus'

export interface ShiftStatusHistoryItem {
    uuid: string
    status: ShiftStatus
    occurredAt: string
    changedBy?: { fullName?: string | null; shortName?: string | null } | null
    // Знімок даних відкриття (лише в записі «Розпочата»; у старих записах — відсутній)
    vehicle?: { uuid?: string | null; title?: string | null } | null
    plannedVehicle?: { uuid?: string | null; title?: string | null } | null
    startOdometer?: number | null
    documentsConfirmed?: boolean | null
    safetyBriefingConfirmed?: boolean | null
    // Знімок даних закриття (лише в записі «Завершена»; у старих записах — відсутній)
    endOdometer?: number | null
    technicalRemarks?: string | null
    /** Коли водій підтвердив закриття в PWA; null — закрито не водієм або до появи підтвердження */
    confirmedAt?: string | null
}

/**
 * Історія статусів зміни (як у задачах). Нові записи першими — так віддає бекенд.
 * На відміну від задач, помилку завантаження не ховаємо за «Історія порожня».
 */
export class ShiftStatusHistoryStore {
    items: ShiftStatusHistoryItem[] = []
    loading = false
    error: string | null = null

    /** Відповідь на застарілий запит (зміна uuid/статусу під час завантаження) ігнорується */
    private requestId = 0

    /** Зміна, чия історія в items: при переході на іншу зміну старий таймлайн не показуємо */
    private shiftUuid = ''

    constructor() {
        makeAutoObservable<this, 'requestId' | 'shiftUuid'>(this, { requestId: false, shiftUuid: false })
    }

    async load(shiftUuid: string): Promise<void> {
        if (!shiftUuid) return
        const id = ++this.requestId
        if (shiftUuid !== this.shiftUuid) {
            this.shiftUuid = shiftUuid
            this.items = []
        }
        this.loading = true
        this.error = null
        try {
            // forceRefresh: після «Розпочати» / «Завершити» кеш restClient віддав би стару історію
            const data = (await restClient.get(
                `/services/shifts/${shiftUuid}/status-history`,
                {},
                { forceRefresh: true }
            )) as { items?: ShiftStatusHistoryItem[] } | null
            if (id !== this.requestId) return
            runInAction(() => {
                this.items = Array.isArray(data?.items) ? data.items : []
            })
        } catch {
            if (id !== this.requestId) return
            runInAction(() => {
                this.items = []
                this.error = 'Не вдалося завантажити історію статусів'
            })
        } finally {
            if (id === this.requestId) {
                runInAction(() => {
                    this.loading = false
                })
            }
        }
    }
}

/** Автор запису: null — зміну створив обмін з ERP або історію відновлено (автор невідомий) */
export const changedByLabel = (item: ShiftStatusHistoryItem): string =>
    item.changedBy?.fullName || item.changedBy?.shortName || '—'

/**
 * Рядки знімка відкриття зміни для таймлайну: ТЗ (і запланований, якщо водій обрав інший),
 * початковий одометр, підтвердження. Порожньо — запис без знімка.
 */
export const startSnapshotLines = (item: ShiftStatusHistoryItem): string[] => {
    // Поля знімка відсутні (undefined) або null у записах без знімка
    const hasSnapshot = [item.vehicle, item.startOdometer, item.documentsConfirmed].some(
        (value) => value !== null && value !== undefined
    )
    if (item.status !== 'started' || !hasSnapshot) return []

    const confirmed = (value: boolean | null | undefined): string =>
        value === true ? 'підтверджено' : value === false ? 'не підтверджено' : '—'
    const vehicle = item.vehicle?.title ?? '—'
    const planned = item.plannedVehicle
    const replaced =
        planned?.uuid && planned.uuid !== item.vehicle?.uuid ? ` (заплановано: ${planned.title ?? '—'})` : ''

    return [
        `ТЗ: ${vehicle}${replaced}`,
        `Початковий одометр: ${typeof item.startOdometer === 'number' ? `${item.startOdometer} км` : '—'}`,
        `Документи: ${confirmed(item.documentsConfirmed)}`,
        `Інструктаж з безпеки руху: ${confirmed(item.safetyBriefingConfirmed)}`,
    ]
}

/**
 * Рядки знімка закриття зміни: кінцевий одометр, зауваження (якщо були) і момент, коли
 * водій підтвердив закриття. Порожньо — запис без знімка (старі записи).
 */
export const completeSnapshotLines = (item: ShiftStatusHistoryItem): string[] => {
    if (item.status !== 'completed') return []

    const lines: string[] = []
    if (typeof item.endOdometer === 'number') lines.push(`Кінцевий одометр: ${item.endOdometer} км`)
    if (item.technicalRemarks) lines.push(`Зауваження до технічного стану ТЗ: ${item.technicalRemarks}`)
    const confirmed = formatAppDate(item.confirmedAt ?? null, 'DD.MM.YYYY, HH:mm')
    if (confirmed) lines.push(`Закриття підтверджено водієм ${confirmed}`)
    return lines
}
